import httpClient from './httpClient';

const REGIMENES_LABORALES_ENDPOINT = '/regimenes-laborales';

/**
 * Servicio para gestionar regímenes laborales
 */
const regimenLaboralService = {
  /**
   * Obtener todos los regímenes laborales
   */
  async getAll() {
    try {
      console.log('🔄 Obteniendo regímenes laborales...');
      const response = await httpClient.get(REGIMENES_LABORALES_ENDPOINT);
      
      if (response.data.success) {
        console.log('✅ Regímenes laborales obtenidos:', response.data.data?.length || 0);
        return {
          success: true,
          data: response.data.data || [],
          message: response.data.message
        };
      }
      
      throw new Error(response.data.message || 'Error al obtener regímenes laborales');
    } catch (error) {
      console.error('❌ Error al obtener regímenes laborales:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Error de conexión'
      };
    }
  },

  /**
   * Obtener un régimen laboral por ID
   */
  async getById(id) {
    try {
      console.log('🔄 Obteniendo régimen laboral:', id);
      const response = await httpClient.get(`${REGIMENES_LABORALES_ENDPOINT}/${id}`);
      
      if (response.data.success) {
        console.log('✅ Régimen laboral obtenido:', response.data.data?.codigo);
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        };
      }
      
      throw new Error(response.data.message || 'Régimen laboral no encontrado');
    } catch (error) {
      console.error('❌ Error al obtener régimen laboral:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || 'Error de conexión'
      };
    }
  },

  /**
   * Crear nuevo régimen laboral
   */
  async create(regimenData) {
    try {
      console.log('🔄 Creando régimen laboral:', regimenData);
      
      // Validar datos requeridos
      if (!regimenData.codigo || !regimenData.nombre) {
        throw new Error('Código y nombre son obligatorios');
      }

      const response = await httpClient.post(REGIMENES_LABORALES_ENDPOINT, regimenData);
      
      if (response.data.success) {
        console.log('✅ Régimen laboral creado:', response.data.data?.codigo);
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        };
      }
      
      throw new Error(response.data.message || 'Error al crear régimen laboral');
    } catch (error) {
      console.error('❌ Error al crear régimen laboral:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || 'Error de conexión',
        errors: error.response?.data?.errors || {}
      };
    }
  },

  /**
   * Actualizar régimen laboral
   */
  async update(id, regimenData) {
    try {
      console.log('🔄 Actualizando régimen laboral:', id, regimenData);
      
      // Validar datos requeridos
      if (!regimenData.codigo || !regimenData.nombre) {
        throw new Error('Código y nombre son obligatorios');
      }

      const response = await httpClient.put(`${REGIMENES_LABORALES_ENDPOINT}/${id}`, regimenData);
      
      if (response.data.success) {
        console.log('✅ Régimen laboral actualizado:', response.data.data?.codigo);
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        };
      }
      
      throw new Error(response.data.message || 'Error al actualizar régimen laboral');
    } catch (error) {
      console.error('❌ Error al actualizar régimen laboral:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || 'Error de conexión',
        errors: error.response?.data?.errors || {}
      };
    }
  },

  /**
   * Eliminar régimen laboral
   */
  async delete(id) {
    try {
      console.log('🔄 Eliminando régimen laboral:', id);
      const response = await httpClient.delete(`${REGIMENES_LABORALES_ENDPOINT}/${id}`);
      
      if (response.data.success) {
        console.log('✅ Régimen laboral eliminado:', id);
        return {
          success: true,
          message: response.data.message
        };
      }
      
      throw new Error(response.data.message || 'Error al eliminar régimen laboral');
    } catch (error) {
      console.error('❌ Error al eliminar régimen laboral:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error de conexión'
      };
    }
  },

  /**
   * Buscar regímenes laborales
   */
  async search(query) {
    try {
      console.log('🔍 Buscando regímenes laborales:', query);
      const response = await httpClient.get(`${REGIMENES_LABORALES_ENDPOINT}/search`, {
        params: { q: query }
      });
      
      if (response.data.success) {
        console.log('✅ Búsqueda completada:', response.data.data?.length || 0, 'resultados');
        return {
          success: true,
          data: response.data.data || [],
          message: response.data.message
        };
      }
      
      throw new Error(response.data.message || 'Error en la búsqueda');
    } catch (error) {
      console.error('❌ Error en búsqueda:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Error de conexión'
      };
    }
  },

  /**
   * Validar datos del régimen laboral
   */
  validateRegimenData(data) {
    const errors = {};

    if (!data.codigo || data.codigo.trim() === '') {
      errors.codigo = 'El código es obligatorio';
    } else if (data.codigo.length > 20) {
      errors.codigo = 'El código no puede exceder 20 caracteres';
    } else if (!/^[A-Z0-9_-]+$/.test(data.codigo)) {
      errors.codigo = 'El código solo puede contener letras mayúsculas, números, guiones y guiones bajos';
    }

    if (!data.nombre || data.nombre.trim() === '') {
      errors.nombre = 'El nombre es obligatorio';
    } else if (data.nombre.length > 200) {
      errors.nombre = 'El nombre no puede exceder 200 caracteres';
    }

    if (data.descripcion && data.descripcion.length > 1000) {
      errors.descripcion = 'La descripción no puede exceder 1000 caracteres';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  /**
   * Formatear régimen para mostrar
   */
  formatRegimen(regimen) {
    if (!regimen) return null;

    return {
      ...regimen,
      codigoFormateado: `[${regimen.codigo}] ${regimen.nombre}`,
      descripcionCorta: regimen.descripcion && regimen.descripcion.length > 100 
        ? regimen.descripcion.substring(0, 100) + '...' 
        : regimen.descripcion || 'Sin descripción',
      fechaCreacion: regimen.created_at ? new Date(regimen.created_at).toLocaleDateString('es-PE') : '',
      fechaActualizacion: regimen.updated_at ? new Date(regimen.updated_at).toLocaleDateString('es-PE') : ''
    };
  },

  /**
   * Obtener tipos de regímenes más comunes
   */
  getTiposComunes() {
    return [
      { codigo: 'RG', nombre: 'Régimen General', descripcion: 'Régimen laboral común del sector privado' },
      { codigo: 'MYPE', nombre: 'Régimen MYPE', descripcion: 'Micro y Pequeñas Empresas' },
      { codigo: 'AGRARIO', nombre: 'Régimen Agrario', descripcion: 'Actividades agrarias y agroexportación' },
      { codigo: 'PESQUERO', nombre: 'Régimen Pesquero', descripcion: 'Actividades pesqueras y acuícolas' },
      { codigo: 'CONSTRUCCION', nombre: 'Régimen de Construcción Civil', descripcion: 'Trabajadores de construcción civil' }
    ];
  }
};

export default regimenLaboralService; 